import { Component, effect, inject, OnDestroy, OnInit, Signal, signal } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import { layerCategory, LayerStandard } from '@core/models/layers.model'
import { UploadedLayersService } from '@core/services/layers-uploaded.service'
import { LayersService } from '@core/services/layers.service'
import { MapComponent } from '@pages/map/map.component'
import { filterLayerOnCategoryPipe } from '@shared/pipes/filter-layer-on-category.pipe'
import Keycloak from 'keycloak-js'
import { CardLayerStandardComponent } from './card-layer-standard/card-layer-standard.component'
import { PersonalLayersComponent } from './personal-layers/personal-layers.component'
import { SharedLayersComponent } from './shared-layers/shared-layers.component'

@Component({
  selector: 'app-layers-tab',
  templateUrl: './layers-tab.component.html',
  imports: [
    // Components
    SharedLayersComponent,
    PersonalLayersComponent,
    CardLayerStandardComponent,

    // Pipes
    filterLayerOnCategoryPipe,

    // Form
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,

    // Material
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatCardModule,
  ],
})
export class LayersTabComponent implements OnInit, OnDestroy {
  readonly keycloak = inject(Keycloak)
  mapComponent = inject(MapComponent)
  layersService = inject(LayersService)
  private readonly _uploadedLayersService = inject(UploadedLayersService)

  isConnected = false
  expanded = true

  categories$ = signal<layerCategory[]>([])

  search$ = this.layersService.search$
  layers$: Signal<LayerStandard[]> = this.layersService.layers$
  availableGeoserverLayerListTitlesState$ =
    this.layersService.availableGeoserverLayerListTitlesState$
  filteredLayers$: Signal<LayerStandard[]> = this.layersService.filteredLayers$
  availableLayers$: Signal<LayerStandard[]> = this.layersService.availableLayers$
  filteredPersonalLayers$ = this._uploadedLayersService.filteredPersonalLayers$
  filteredSharedLayers$ = this._uploadedLayersService.filteredSharedLayers$

  constructor() {
    effect(() => {
      // Expand categories with selected layers by snapshot
      const layers = this.filteredLayers$()
      const categories = this.categories$()
      categories.forEach((category) => {
        const hasSelectedLayers = layers
          .filter((layer) => layer.category === category.name)
          .some((layer) => layer.isSelected)

        if (hasSelectedLayers) {
          category.expanded = true
        }
      })
    })
  }

  ngOnDestroy() {
    this.search$.set('')
  }

  async ngOnInit() {
    this.isConnected = this.keycloak.authenticated ?? false
    this._getLayerAndCategory(this.layers$())

    if (
      this.isConnected &&
      this._uploadedLayersService.personalLayersState$().status !== 'success'
    ) {
      await this._uploadedLayersService.getUserPersonalAndSharedLayers()
    }
  }

  private _getLayerAndCategory(layers: LayerStandard[]) {
    const uniqueCategories = Array.from(new Set(layers.map((layer) => layer.category)))
    const categories = uniqueCategories.map((categoryName) => ({
      name: categoryName,
      expanded: true,
    }))
    this.categories$.set(categories)
  }

  clearSearch() {
    this.search$.set('')
  }

  unselectAllLayers() {
    if (this.isConnected) {
      this._uploadedLayersService.unCheckAllUploadedLayers()
    }

    this.layersService.removeAllLayers()
  }

  categoryHasSelectedLayers(categoryName: string): boolean {
    return this.filteredLayers$()
      .filter((layer) => layer.category === categoryName)
      .some((layer) => layer.isSelected)
  }
}
